import React from "react";
import { graphql, useFragment } from "react-relay";

import { faChevronDown, faChevronUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import EventEmitter from "events";
import { throttle } from "lodash";

import { useForwardedRef } from "~/lib/hooks";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import IconButton from "~/ui/legacy/IconButton";

import { useCurrentFullscreenElement } from "../common/Fullscreen";
import { TranscriptModalContext } from "./TranscriptModalContext";
import { useIsNarrowViewport, useIsPublic } from "./utils";
import { Video } from "./Video";

import type { TranscriptVideo_meetingRecording$key } from "~/__generated__/TranscriptVideo_meetingRecording.graphql";
import type { TranscriptVideo_videoPlaybackSettings$key } from "~/__generated__/TranscriptVideo_videoPlaybackSettings.graphql";
import type { RecordingMediaType } from "~/__generated__/Video_meetingRecording.graphql";

import styles from "./css/TranscriptModal.module.css";

const videoEmitter = new EventEmitter();

interface TranscriptVideoProps extends Omit<React.HTMLProps<HTMLVideoElement>, "ref"> {
	meetingRecording: TranscriptVideo_meetingRecording$key;
	videoPlaybackSettings: TranscriptVideo_videoPlaybackSettings$key;
	initialVideoTimestamp?: number;
	isMiniPlayerActive?: boolean;
	toggleMiniPlayer?: () => void;
	thumbnailTrack: TextTrack | null;
	isClip?: boolean;
	inClippingMode: boolean;
	openClipTrimmer?: () => void;
	parts?: { value: string; label: string }[];
	setSelectedRecordingId?: (recordingId: string) => void;
	hideClipButton?: boolean;
	allowRecapDownload?: boolean;
	isCollapsed: boolean;
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

export function saveUserVideoProgress(recordingOrClipId: string, progress: number) {
	commitMutationPromise(environment, {
		mutation: graphql`
			mutation TranscriptVideo_SaveVideoProgressMutation($recordingOrClipId: ID!, $progress: Float!) {
				saveUserMeetingRecordingVideoProgress(recordingOrClipId: $recordingOrClipId, progress: $progress) {
					recordingOrClip {
						id
						__typename
						userVideoProgress
					}
				}
			}
		`,
		variables: {
			recordingOrClipId,
			progress,
		},
	});
}

export default React.forwardRef<HTMLVideoElement, TranscriptVideoProps>(function TranscriptVideo(
	{
		meetingRecording: meetingRecordingKey,
		videoPlaybackSettings: videoPlaybackSettingsKey,
		initialVideoTimestamp,
		toggleMiniPlayer,
		isMiniPlayerActive,
		thumbnailTrack,
		isClip = false,
		inClippingMode,
		isCollapsed,
		setIsCollapsed,
		children,
		...videoProps
	},
	ref,
) {
	const videoRef = useForwardedRef<HTMLVideoElement>(ref);
	const [isFullScreen, setIsFullScreen] = React.useState(false);
	const [rotatedToLandscape, setRotatedToLandscape] = React.useState(false);

	const { analyticsFingerprint, noteId, meetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	const meetingRecording = useFragment(
		graphql`
			fragment TranscriptVideo_meetingRecording on MeetingRecordingInterface
			@argumentDefinitions(public: { type: "Boolean!" }) {
				__typename
				id
				... on MeetingRecording {
					userVideoProgress
					clips @skip(if: $public) {
						id
					}
					didViewerAttend
				}
				...Video_meetingRecording
			}
		`,
		meetingRecordingKey,
	);
	const videoPlaybackSettings = useFragment(
		graphql`
			fragment TranscriptVideo_videoPlaybackSettings on UserVideoPlaybackSettings {
				id
				playbackRate
				subtitlesEnabled
				volume
				muted
			}
		`,
		videoPlaybackSettingsKey,
	);

	const meetingRecordingIdRef = React.useRef(meetingRecording.id);

	const initialTimestampRef = React.useRef(initialVideoTimestamp ?? meetingRecording.userVideoProgress);
	React.useEffect(() => {
		const ts = initialTimestampRef.current;
		if (ts === undefined) return;
		if (videoRef.current) {
			videoRef.current.currentTime = ts;
		}
	}, [videoRef]);

	// On mount, set the video playback settings based on the user's defaults
	const initialVideoPlaybackSettings = React.useRef(videoPlaybackSettings);
	React.useEffect(() => {
		if (!videoRef.current) return;

		const video = videoRef.current;
		const { playbackRate, volume, muted, subtitlesEnabled } = initialVideoPlaybackSettings.current;

		video.playbackRate = playbackRate;
		video.volume = volume;
		video.muted = muted;

		function enableSubtitlesOnLoadedMetadata() {
			if (!video) return;

			const subtitlesTrack = [...video.textTracks].find(track => track.kind === "subtitles");
			if (!subtitlesTrack) return;

			subtitlesTrack.mode = "showing";
		}

		function onVideoPlay(videoId: string) {
			if (videoId !== meetingRecordingIdRef.current) video.pause();
		}

		if (subtitlesEnabled) {
			video.addEventListener("loadedmetadata", enableSubtitlesOnLoadedMetadata, { once: true });
		}

		videoEmitter.on("Video.play", onVideoPlay);

		return () => {
			video.removeEventListener("loadedmetadata", enableSubtitlesOnLoadedMetadata);
			videoEmitter.off("Video.play", onVideoPlay);
		};
	}, [videoRef]);

	const isPublic = useIsPublic();

	React.useEffect(() => {
		const video = videoRef.current;
		if (!video || isPublic) return;

		const throttledUpdateVideoPlaybackSettings = throttle(updateVideoPlaybackSettings, 1000);

		video.addEventListener("play", videoPlaybackListener);
		document.addEventListener("fullscreenchange", fullscreenChangeListener);

		video.addEventListener("ratechange", throttledUpdateVideoPlaybackSettings);
		video.addEventListener("volumechange", throttledUpdateVideoPlaybackSettings);

		let previousSubtitlesMode: TextTrackMode | undefined;
		video.textTracks.addEventListener("change", onTextTrackChange);

		return () => {
			video.removeEventListener("ratechange", throttledUpdateVideoPlaybackSettings);
			video.removeEventListener("volumechange", throttledUpdateVideoPlaybackSettings);
			video.textTracks.removeEventListener("change", onTextTrackChange);
			video.removeEventListener("play", videoPlaybackListener);
			document.removeEventListener("fullscreenchange", fullscreenChangeListener);
		};

		function onTextTrackChange() {
			if (!video) return;
			const subtitlesTrack = [...video.textTracks].find(track => track.kind === "subtitles");
			if (!subtitlesTrack) return;

			if (!previousSubtitlesMode || previousSubtitlesMode !== subtitlesTrack.mode) {
				dispatchAnalyticsTrack("[AI Copilot] Subtitles Toggled", {
					subtitlesMode: subtitlesTrack.mode,
					fingerprint: analyticsFingerprint,
					source: "Modal",
				});
				previousSubtitlesMode = subtitlesTrack.mode;
				throttledUpdateVideoPlaybackSettings();
			}
		}
		function videoPlaybackListener() {
			dispatchAnalyticsTrack("[AI Copilot] Recording Played", {
				fingerprint: analyticsFingerprint,
				source: "Modal",
				/* eslint-disable camelcase */
				note_id: noteId,
				meeting_recoding_summary_id: meetingRecordingSummaryId,
				did_viewer_attend: meetingRecording.didViewerAttend,
				/* eslint-enable camelcase */
			});

			videoEmitter.emit("Video.play", meetingRecordingIdRef.current);
		}
		function fullscreenChangeListener() {
			isFullScreen &&
				dispatchAnalyticsTrack("[AI Copilot] Fullscreen Toggled", {
					fingerprint: analyticsFingerprint,
					source: "Modal",
				});
		}
		async function updateVideoPlaybackSettings() {
			if (!video) return;

			commitMutationPromise(environment, {
				mutation: graphql`
					mutation TranscriptVideo_updateVideoPlaybackSettingsMutation(
						$playbackRate: Float!
						$subtitlesEnabled: Boolean!
						$volume: Float!
						$muted: Boolean!
					) {
						updateUserVideoPlaybackSettings(
							playbackRate: $playbackRate
							subtitlesEnabled: $subtitlesEnabled
							volume: $volume
							muted: $muted
						) {
							userVideoPlaybackSettings {
								...TranscriptVideo_videoPlaybackSettings
							}
						}
					}
				`,
				variables: {
					playbackRate: video.playbackRate,
					subtitlesEnabled: [...video.textTracks].some(track => track.mode === "showing"),
					volume: video.volume,
					muted: video.muted,
				},
			});
		}
	}, [
		videoRef,
		isPublic,
		analyticsFingerprint,
		isFullScreen,
		noteId,
		meetingRecordingSummaryId,
		meetingRecording.didViewerAttend,
	]);

	// Save video progress on unmount or when visibility state becomes hidden
	// NOTE: This needs to happen above the effect below that erases the src
	// attribute on the video on unmount; otherwise we'll reset the user's video
	// progress to 0.
	const lastSavedProgressRef = React.useRef<number | null>(null);

	React.useEffect(() => {
		if (!videoRef.current || isPublic) return;
		const video = videoRef.current;
		const meetingRecordingId = meetingRecordingIdRef.current;

		function saveUserVideoProgressIfChanged() {
			// Only save if the progress has changed
			if (lastSavedProgressRef.current === video.currentTime) {
				return;
			}
			lastSavedProgressRef.current = video.currentTime;
			saveUserVideoProgress(meetingRecordingId, video.currentTime);
		}

		function onVisibilityChange() {
			if (document.visibilityState !== "hidden") return;
			saveUserVideoProgressIfChanged();
		}

		document.addEventListener("visibilitychange", onVisibilityChange);
		video.addEventListener("pause", saveUserVideoProgressIfChanged);
		video.addEventListener("abort", saveUserVideoProgressIfChanged);

		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange);
			video.removeEventListener("pause", saveUserVideoProgressIfChanged);
			video.removeEventListener("abort", saveUserVideoProgressIfChanged);
			saveUserVideoProgressIfChanged();
		};
	}, [videoRef, isPublic]);

	React.useEffect(() => {
		const video = videoRef.current;
		if (!video) return;

		return () => {
			// Delete the src of the video. If we don't do this, pressing the play
			// button on your keyboard will resume the video even after you've closed
			// the modal.
			video.src = "";
		};
	}, [videoRef]);

	const currentFullscreenElement = useCurrentFullscreenElement();
	React.useEffect(() => {
		// If the video is in fullscreen mode, we need to update the state to reflect
		const video = videoRef.current;
		if (!video) return;
		const mediaController = video.closest("media-controller");

		const newFullScreenState = currentFullscreenElement === mediaController;

		// This part wont work in a browser, but should work in a mobile app
		// provided the app manifest is not locked to portrait mode.
		if (!isFullScreen && newFullScreenState) {
			// Use type assertion for screen.orientation to handle the lock method
			const orientation = screen.orientation as { lock?: (orientation: string) => Promise<void> };
			orientation.lock?.("landscape")?.catch((e: unknown) => {
				console.warn("Failed to lock orientation to landscape:", e);
			});
		} else if (isFullScreen && !newFullScreenState) {
			// Use type assertion for screen.orientation to handle the unlock method
			const orientation = screen.orientation as { unlock?: () => void };
			orientation.unlock?.();
		}
		setIsFullScreen(newFullScreenState);
		// If we exit fullscreen for any reason, we should note that we are no longer rotating to landscape
		if (!newFullScreenState) setRotatedToLandscape(false);
		return () => {
			// eslint warns that this API is not available, but we're only using it
			// if it's not undefined so should be OK.
			// eslint-disable-next-line compat/compat
			screen.orientation?.unlock?.();
		};
	}, [currentFullscreenElement, isFullScreen, videoRef]);

	// Attach effect to detect orientation change.
	// If it was previously enabled, and the device is narrow width, resume fullscreen.
	const isNarrowViewport = useIsNarrowViewport();
	React.useEffect(() => {
		const video = videoRef.current;
		if (!video) return;
		const mediaController = video.closest("media-controller");

		function onOrientationChange() {
			if (!document.fullscreenEnabled) return;

			// If the device is going to be in landscape, and the viewport is narrow, then request fullscreen
			if (!isFullScreen && isNarrowViewport && screen.orientation.type.includes("landscape")) {
				// dispatch an event to the media controller so it knows to update the UI
				mediaController?.dispatchEvent(
					new CustomEvent("mediaenterfullscreenrequest", { composed: true, bubbles: true }),
				);
				// Sometimes the above calls the wrong API, so we need to call the correct one
				// @ts-ignore - The non-webkit method doesn't work inside of an orientation change event
				mediaController?.webkitRequestFullscreen?.();
				setRotatedToLandscape(true);
			} else if (rotatedToLandscape) {
				if (document.fullscreenElement) {
					// We put this in an if statement because it's possible that the fullscreen request earlier failed.
					mediaController?.dispatchEvent(
						new CustomEvent("mediaexitfullscreenrequest", { composed: true, bubbles: true }),
					);
				}
				setRotatedToLandscape(false);
			}
		}
		window.addEventListener("orientationchange", onOrientationChange);
		return () => {
			window.removeEventListener("orientationchange", onOrientationChange);
		};
	}, [isFullScreen, isNarrowViewport, videoRef, rotatedToLandscape]);

	// Attach event listeners for public view analytics
	React.useEffect(() => {
		const video = videoRef.current;
		if (!video || !isPublic) return;

		video.addEventListener("play", videoPlaybackListener);
		document.addEventListener("fullscreenchange", fullscreenChangeListener);

		let previousSubtitlesMode: TextTrackMode | undefined;
		video.textTracks.addEventListener("change", onTextTrackChange);
		return () => {
			video.textTracks.removeEventListener("change", onTextTrackChange);
			video.removeEventListener("play", videoPlaybackListener);
			document.removeEventListener("fullscreenchange", fullscreenChangeListener);
		};

		function onTextTrackChange() {
			if (!video) return;
			const subtitlesTrack = [...video.textTracks].find(track => track.kind === "subtitles");
			if (!subtitlesTrack) return;

			if (!previousSubtitlesMode || previousSubtitlesMode !== subtitlesTrack.mode) {
				dispatchAnalyticsTrack("[AI Copilot] Subtitles Toggled", {
					subtitlesMode: subtitlesTrack.mode,
					fingerprint: analyticsFingerprint,
					source: "Public View",
				});
				previousSubtitlesMode = subtitlesTrack.mode;
			}
		}
		function videoPlaybackListener() {
			dispatchAnalyticsTrack("[AI Copilot] Recording Played", {
				fingerprint: analyticsFingerprint,
				source: "Public View",
				/* eslint-disable camelcase */
				note_id: noteId,
				meeting_recoding_summary_id: meetingRecordingSummaryId,
				/* eslint-enable camelcase */
			});
		}
		function fullscreenChangeListener() {
			isFullScreen &&
				dispatchAnalyticsTrack("[AI Copilot] Fullscreen Toggled", {
					fingerprint: analyticsFingerprint,
					source: "Public View",
				});
		}
	}, [analyticsFingerprint, isFullScreen, isPublic, meetingRecordingSummaryId, noteId, videoRef]);

	return (
		<Video
			ref={videoRef}
			className={cx(styles.video, { [styles.videoMiniPlayer]: isMiniPlayerActive })}
			isMiniPlayerActive={isMiniPlayerActive}
			toggleMiniPlayer={toggleMiniPlayer}
			thumbnailTrack={thumbnailTrack}
			numClips={meetingRecording.clips?.length ?? 0}
			isClip={isClip}
			inClippingMode={inClippingMode}
			meetingRecordingKey={meetingRecording}
			isCollapsed={isCollapsed}
			setIsCollapsed={setIsCollapsed}
			{...videoProps}
		>
			{children}
		</Video>
	);
});

export function CollapseButton({
	isCollapsed,
	onClick,
	mediaType,
}: {
	isCollapsed: boolean;
	onClick: () => void;
	mediaType?: RecordingMediaType;
}) {
	if (mediaType !== "VIDEO") return null;

	return (
		<div className={styles.collapseButton}>
			<div className={styles.divider}></div>
			<IconButton onClick={onClick} className={styles.arrowButton} icon={isCollapsed ? faChevronDown : faChevronUp} />
			<div className={styles.divider}></div>
		</div>
	);
}
